import Grid from "@material-ui/core/Grid";
import LinearProgress from "@material-ui/core/LinearProgress";
import Typography from "@material-ui/core/Typography";
import AssignmentIndOutlined from "@material-ui/icons/AssignmentIndOutlined";
import MeetingRoom from "@material-ui/icons/MeetingRoom";
import People from "@material-ui/icons/People";
import SchoolOutlined from "@material-ui/icons/SchoolOutlined";
import React, { FC, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import { routesPaths } from "../../Routes";
import {
  useCreateExamDayMutation,
  useDeleteExamDayMutation,
  useDeleteRoomAssignmentMutation,
  useGetExamSessionQuery,
  useUpdateExamDayMutation,
} from "../../api/rtk/ExamSessionApi";
import { NewEntityId, SwissGermanLocale, SwissTimeZone, UserRoomAssignmentType } from "../../data/constants";
import { ExamDayType } from "../../model/ExamDayType";
import { ExamDayViewModel } from "../../model/ExamDayViewModel";
import { GetLocalizedName } from "../../model/ExamSessionViewModel";
import { TimeSlotStructureType } from "../../model/TimeSlotStructure";
import TitleWithIcon from "../common/TitleWithIcon";
import CustomButton from "../common/buttons/CustomButton";
import DefaultButtons from "../common/buttons/DefaultButtons";
import RoomAssignmentList from "../roomAssignment/RoomAssignmentList";
import TranslatedMessage from "../translations/TranslatedMessage";
import ExamDayFields from "./ExamDayFields";

const ExamDayForm: FC = () => {
  const history = useHistory();
  const { examSessionId, locationId, examDayId } =
    useParams<{ examSessionId: string; locationId: string; examDayId: string }>();
  const { data: examSession, isFetching: isExamSessionFetching } = useGetExamSessionQuery(examSessionId);
  const [deleteRoomAssignment, { isLoading: isDeleteRoomAssignmentLoading }] = useDeleteRoomAssignmentMutation();
  const [deleteExamDay, { isLoading: isDeleteExamDayLoading }] = useDeleteExamDayMutation();
  const [createExamDay, { isLoading: isCreateExamDayLoading }] = useCreateExamDayMutation();
  const [updateExamDay, { isLoading: isUpdateExamDayLoading }] = useUpdateExamDayMutation();

  const location = examSession?.locations.find((location) => location.id === locationId);

  const [examDay, setExamDay] = useState<ExamDayViewModel>({
    id: NewEntityId,
    date: new Date(),
    locationId,
    roomAssignments: [],
    type: ExamDayType.Written,
    availableUsers: [],
    availableCorrectionExperts: [],
    timeSlotStructureType: TimeSlotStructureType.ElectiveWritten,
  });

  useEffect(() => {
    const examDay = examSession?.examDays.find((examDay) => examDay.id === examDayId);
    if (examDay !== undefined) {
      setExamDay(examDay);
    }
    else if (examSession !== undefined) {
      const now = new Date();
      const utcToday = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
      const utcFrom = new Date(examSession.from);
      const utcTo = new Date(examSession.to);
      setExamDay(examDay => ({
        ...examDay,
        date: utcToday >= utcFrom && utcToday <= utcTo ? utcToday : utcFrom
      }))
    }

  }, [examDayId, examSession]);

  if (
    isExamSessionFetching ||
    isDeleteRoomAssignmentLoading ||
    isDeleteExamDayLoading ||
    isCreateExamDayLoading ||
    isUpdateExamDayLoading
  ) {
    return <LinearProgress variant="query" />;
  }

  if (examSession === undefined || examDay === undefined || location === undefined) {
    return <></>;
  }

  const examDayDate = new Date(examDay.date).toLocaleDateString(SwissGermanLocale, { timeZone: SwissTimeZone });
  const from = new Date(examSession.from);
  const to = new Date(examSession.to);

  const close = () => history.push(routesPaths.examSessionLocation(examSessionId, locationId));

  const handleChange = (partialExamDay: Partial<ExamDayViewModel>) =>
    setExamDay((prevExamDay) => ({ ...prevExamDay, ...partialExamDay }));

  const handleClose = () => close();

  const handleSave = async () => {
    var requestData = { examSessionId, data: examDay };
    const response = await (examDay.id === NewEntityId
      ? createExamDay(requestData)
      : updateExamDay(requestData)
    ).unwrap();
    if (response.success) {
      close();
    }
  };

  const handleDelete = async () => {
    const response = await deleteExamDay({ examSessionId, examDayId: examDay.id }).unwrap();
    if (response.success) {
      close();
    }
  };

  const handleStartAssigningRoomAssignment = () =>
    history.push(routesPaths.roomAssignment(examSessionId, locationId, examDay.id, NewEntityId));

  const handleDeleteRoomAssignment = async (roomAssignmentId: string) => {
    const response = await deleteRoomAssignment({ examSessionId, examDayId, roomAssignmentId }).unwrap();
    if (response.success) {
      close();
    }
  };

  const assignProctorsToWrittenExamsButton = (
    <CustomButton
      tooltip={<TranslatedMessage id="examDay.button.assignProctorsToWrittenExams" />}
      onClick={() =>
        history.push(routesPaths.userRoomAssignment(examSessionId, examDay.id, UserRoomAssignmentType.WrittenExam))
      }
    >
      <AssignmentIndOutlined />
    </CustomButton>
  );

  const assignProctorsToOralPreparationButton = (
    <CustomButton
      tooltip={<TranslatedMessage id="examDay.button.assignProctorsToOralPreparations" />}
      onClick={() =>
        history.push(routesPaths.userRoomAssignment(examSessionId, examDay.id, UserRoomAssignmentType.OralPreparation))
      }
    >
      <SchoolOutlined />
    </CustomButton>
  );

  const assignExpertsToOralExamButton = (
    <CustomButton
      tooltip={<TranslatedMessage id="examDay.button.assignExpertsToOralExams" />}
      onClick={() =>
        history.push(routesPaths.userRoomAssignment(examSessionId, examDay.id, UserRoomAssignmentType.OralExam))
      }
    >
      <People />
    </CustomButton>
  );

  const assignExpertsToCorrectionExamsButton = (
    <CustomButton
      tooltip={<TranslatedMessage id="examDay.button.assignExpertsToCorrectionExams" />}
      onClick={() =>
        history.push(routesPaths.userRoomAssignment(examSessionId, examDay.id, UserRoomAssignmentType.Correction))
      }
    >
      <AssignmentIndOutlined />
    </CustomButton>
  );

  return (
    <>
      <Grid container={true} direction="column" spacing={3}>
        <Grid item={true} container={true} spacing={0} alignItems="baseline">
          <Grid item={true} xs={12} sm={6}>
            <Typography variant="h6" component="span">
              {GetLocalizedName(examSession)} - {location.name}
            </Typography>
            <TitleWithIcon title={examDayDate} variant="h4">
              <MeetingRoom />
            </TitleWithIcon>
          </Grid>
          <Grid item={true} xs={12} sm={6}>
            <DefaultButtons
              onClose={handleClose}
              onDelete={handleDelete}
              onSave={handleSave}
              isDeleteButtonEnabled={examDay.id !== NewEntityId && !examSession.published}
              isSaveButtonEnabled={!examSession.published}
              additionalButtons={
                <>
                  {examDay.type === ExamDayType.Written &&
                    examDay.roomAssignments.length > 0 &&
                    assignProctorsToWrittenExamsButton}
                  {examDay.type === ExamDayType.Oral &&
                    examDay.roomAssignments.find((assignment) => assignment.isPreparationRoom) &&
                    assignProctorsToOralPreparationButton}
                  {examDay.type === ExamDayType.Oral &&
                    examDay.roomAssignments.find((assignment) => !assignment.isPreparationRoom) &&
                    assignExpertsToOralExamButton}
                  {examDay.type === ExamDayType.Correction &&
                    examDay.roomAssignments.length > 0 &&
                    assignExpertsToCorrectionExamsButton}
                </>
              }
            />
          </Grid>
        </Grid>
        <Grid item={true}>
          <ExamDayFields
            examDay={examDay}
            onPropertyValueChanges={handleChange}
            minDate={from}
            maxDate={to}
          />
        </Grid>
        <Grid item={true} container={true}>
          {examDay.id !== NewEntityId && (
            <RoomAssignmentList
              examSession={examSession}
              isReadOnly={examSession.published}
              locationId={location.id}
              onDeleteRoomAssignment={handleDeleteRoomAssignment}
              roomAssignments={examDay.roomAssignments}
              onStartAddingRoomAssignment={handleStartAssigningRoomAssignment}
            />
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default ExamDayForm;
